import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingButton } from "@mui/lab";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Localized } from "@fluent/react";
import { useCallback, useState } from "react";
import { DeviceSelectField } from "../../../device/DeviceSelectField";
import { Device } from "../../../device/deviceSlice";
import { APIError, AppId } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { useAppDispatch } from "../../../../app/hooks";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { linkValidationProjectDevice, ValidationProjectDeviceDto } from "../validationProjectSlice";

export interface LinkValidationProjectDeviceDialogProps {
    projectId: AppId;
    onSave: (device: ValidationProjectDeviceDto) => void;
    onCancel: () => void;
}

export const LinkValidationProjectDeviceDialog = ({ projectId, onSave, onCancel }: LinkValidationProjectDeviceDialogProps) => {
    const dispatch = useAppDispatch();
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
    const [, setSelectedDevice] = useState<Device | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<APIError | null>(null);

    const handleSaveClick = useCallback(() => {
        if (!selectedDeviceId) {
            return;
        }

        setSaving(true);
        setError(null);
        dispatch(linkValidationProjectDevice({
            id: projectId,
            payload: {
                deviceId: Number(selectedDeviceId),
                order: null,
                description: "",
            },
        }))
            .then(unwrapResult)
            .then((device) => onSave(device))
            .catch((err: APIError) => setError(err))
            .finally(() => setSaving(false));
    }, [dispatch, onSave, projectId, selectedDeviceId]);

    return <Dialog open fullWidth maxWidth="md">
        <DialogTitle>
            <Localized id="validation-project-link-device">
                <span>Podlinkuj urządzenie</span>
            </Localized>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
            <DeviceSelectField
                value={selectedDeviceId}
                onChange={setSelectedDeviceId}
                onSelectDevice={setSelectedDevice}
            />
            <ServerErrorMsg err={error} />
        </DialogContent>
        <DialogActions>
            <SaveButton loading={saving} onClick={handleSaveClick} disabled={!selectedDeviceId} />

            <LoadingButton loading={saving} onClick={onCancel}>
                <Localized id="cancel"><span>Anuluj</span></Localized>
            </LoadingButton>
        </DialogActions>
    </Dialog>;
};